import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';

const PAGE_TABS = [
  { key: 'public_home', label: 'Public home', hint: 'Shown on the main public home page.' },
  { key: 'player_home', label: 'Player dashboard', hint: 'For players & captains.' },
  { key: 'admin_home', label: 'Admin dashboard', hint: 'Your logged in dashboard.' },
  { key: 'admin_stats', label: 'League dashboards', hint: 'Widgets shown when switching to a league tab.' },
];

const TYPE_LABELS = {
  message: 'Message',
  quick_links: 'Quick links',
  ongoing_leagues: 'Active leagues',
  featured_fixtures: 'Featured fixtures',
  league_table: 'League table',
  league_milestones: 'League milestones',
  title_race: 'Title race',
  tons_race: 'Tons race',
  next_fixtures: 'Next fixtures',
  player_summary: 'Player summary',
};

function makeDefaultConfig(type, activeKey) {
  if (type === 'message') return { title: '', body: '', tone: 'neutral', mdColSpan: 2, lgColSpan: 3 };
  if (type === 'quick_links') return { links: [{ label: 'Example', href: '/fixtures', icon: 'fa-link' }], mdColSpan: 1, lgColSpan: 1 };
  if (type === 'ongoing_leagues') return { title: 'Active leagues', mdColSpan: 1, lgColSpan: 1 };
  if (type === 'featured_fixtures') {
    return {
      title: 'Featured fixtures',
      limit: 8,
      scope: activeKey === 'player_home' ? 'my_team' : 'league_wide',
      league: 'PDL',
      season: '',
      fixtureIds: [],
      mdColSpan: 2,
      lgColSpan: 3,
    };
  }
  if (type === 'next_fixtures') {
    return {
      title: 'Next fixtures',
      limit: 8,
      scope: activeKey === 'player_home' ? 'my_team' : 'league_wide',
      league: 'PDL',
      season: '',
      fixtureIds: [],
      mdColSpan: 2,
      lgColSpan: 2,
    };
  }
  if (type === 'league_table') return { title: 'League table', league: 'PDL', season: '', limit: 10, mdColSpan: 1, lgColSpan: 1 };
  if (type === 'league_milestones') return { title: 'League milestones', league: 'PDL', season: '', mdColSpan: 1, lgColSpan: 1 };
  if (type === 'title_race') return { title: 'Title race', league: 'PDL', season: '', limit: 6, mdColSpan: 1, lgColSpan: 1 };
  if (type === 'tons_race') return { title: 'Tons race', league: 'PDL', season: '', limit: 10, mdColSpan: 1, lgColSpan: 1 };
  if (type === 'player_summary') return { title: 'Player summary', league: 'PDL', season: '', mdColSpan: 2, lgColSpan: 3 };
  return { mdColSpan: 1, lgColSpan: 1 };
}

function defaultWidgetsForAdminStats(layoutKey) {
  const lk = String(layoutKey || 'default').trim() || 'default';
  const main = lk === 'default' || lk === 'combined';
  const league = main
    ? 'PDL'
    : String(lk.replace(/^league:/i, '') || 'PDL')
        .toUpperCase()
        .slice(0, 32) || 'PDL';
  if (main) {
    return [
      {
        id: 'as-msg',
        type: 'message',
        order: 0,
        config: {
          title: 'Account overview',
          body:
            'This strip is account-wide: it does not change when you switch the league in the header. Use a league tab for a saved strip scoped to that competition. Season charts and stats below still follow the header league.',
          tone: 'brand',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      { id: 'as-ol', type: 'ongoing_leagues', order: 1, config: { title: 'Active leagues', mdColSpan: 2, lgColSpan: 2 } },
      {
        id: 'as-ps',
        type: 'player_summary',
        order: 2,
        config: {
          title: 'Your player snapshot',
          league: 'PDL',
          season: '',
          mdColSpan: 2,
          lgColSpan: 2,
        },
      },
      {
        id: 'as-ql',
        type: 'quick_links',
        order: 3,
        config: {
          links: [
            { label: 'Fixtures', href: '/admin/fixtures', icon: 'fa-calendar-days' },
            { label: 'League table', href: '/admin/league-table', icon: 'fa-ranking-star' },
            { label: 'Account settings', href: '/admin/account-settings', icon: 'fa-gear' },
          ],
          mdColSpan: 2,
          lgColSpan: 2,
        },
      },
    ];
  }
  return [
    {
      id: 'as2-msg',
      type: 'message',
      order: 0,
      config: {
        title: `${league} dashboard`,
        body: `League-focused widgets for ${league}. Edit this layout in Account settings (Admin stats, per league).`,
        tone: 'brand',
        mdColSpan: 2,
        lgColSpan: 3,
      },
    },
    {
      id: 'as2-nx',
      type: 'next_fixtures',
      order: 1,
      config: {
        title: 'Next fixtures',
        limit: 8,
        scope: 'league_wide',
        league,
        season: '',
        fixtureIds: [],
        mdColSpan: 2,
        lgColSpan: 2,
      },
    },
    {
      id: 'as2-tb',
      type: 'league_table',
      order: 2,
      config: { title: 'League table', league, season: '', limit: 12, mdColSpan: 2, lgColSpan: 2 },
    },
    {
      id: 'as2-tr',
      type: 'title_race',
      order: 3,
      config: { title: 'Title race', league, season: '', limit: 6, mdColSpan: 1, lgColSpan: 1 },
    },
    {
      id: 'as2-tons',
      type: 'tons_race',
      order: 4,
      config: { title: 'Tons race', league, season: '', limit: 8, mdColSpan: 1, lgColSpan: 1 },
    },
    {
      id: 'as2-ql',
      type: 'quick_links',
      order: 5,
      config: {
        links: [
          { label: 'Fixtures', href: '/admin/fixtures', icon: 'fa-calendar-days' },
          { label: 'League table', href: '/admin/league-table', icon: 'fa-ranking-star' },
        ],
        mdColSpan: 1,
        lgColSpan: 1,
      },
    },
  ];
}

function defaultWidgetsFor(key, layoutKey = 'default') {
  if (key === 'admin_home') {
    return [
      {
        id: 'admin-home-control-panel',
        type: 'message',
        order: 0,
        config: {
          sectionKey: 'control_panel',
          enabled: true,
          title: 'Control panel quick links',
          body: '',
          tone: 'brand',
          mdColSpan: 2,
          lgColSpan: 3,
          shortcutTarget: '',
          shortcutTitle: '',
          shortcutIcon: '',
        },
      },
      {
        id: 'admin-home-challenges-training',
        type: 'message',
        order: 1,
        config: {
          sectionKey: 'challenges_training',
          enabled: true,
          title: 'Challenges & training',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'admin-home-plan-shortcuts',
        type: 'message',
        order: 2,
        config: {
          sectionKey: 'plan_shortcuts',
          enabled: true,
          title: 'Plan shortcuts',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'admin-home-subscription',
        type: 'message',
        order: 3,
        config: {
          sectionKey: 'subscription',
          enabled: true,
          title: 'Subscription',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'admin-home-backup-restore',
        type: 'message',
        order: 4,
        config: {
          sectionKey: 'backup_restore',
          enabled: true,
          title: 'Backup & restore',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'admin-home-upcoming-by-league',
        type: 'message',
        order: 5,
        config: {
          sectionKey: 'upcoming_by_league',
          enabled: true,
          title: 'Upcoming by league',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'admin-home-admin-stats',
        type: 'message',
        order: 6,
        config: {
          sectionKey: 'admin_stats',
          enabled: true,
          title: 'Admin stats',
          body: '',
          tone: 'neutral',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
    ];
  }

  if (key === 'admin_stats') return defaultWidgetsForAdminStats(layoutKey);
  if (key === 'player_home') {
    return [
      {
        id: 'ph-summary',
        type: 'player_summary',
        order: 0,
        config: {
          title: 'Player summary',
          league: 'PDL',
          season: '',
          mdColSpan: 2,
          lgColSpan: 3,
        },
      },
      {
        id: 'ph-msg',
        type: 'message',
        order: 1,
        config: {
          title: 'Your league hub',
          body: 'Fixtures, results, and quick links below are curated by league admins.',
          tone: 'brand',
          mdColSpan: 1,
          lgColSpan: 1,
        },
      },
      {
        id: 'ph-next',
        type: 'next_fixtures',
        order: 2,
        config: {
          title: 'Upcoming for your team',
          limit: 8,
          scope: 'my_team',
          league: 'PDL',
          season: '',
          fixtureIds: [],
          mdColSpan: 2,
          lgColSpan: 2,
        },
      },
      {
        id: 'ph-links',
        type: 'quick_links',
        order: 3,
        config: {
          links: [
            { label: 'All fixtures', href: '/admin/fixtures', icon: 'fa-calendar-days' },
            { label: 'League table', href: '/admin/league-table', icon: 'fa-ranking-star' },
            { label: 'Venues', href: '/venues', icon: 'fa-map-location-dot' },
            { label: 'League rules', href: '/rules', icon: 'fa-book' },
          ],
          mdColSpan: 1,
          lgColSpan: 1,
        },
      },
    ];
  }

  return [
    { id: 'pub-msg', type: 'message', order: 0, config: { title: 'This week in the league', body: 'Browse ongoing competitions, upcoming fixtures, and standings.', tone: 'brand', mdColSpan: 2, lgColSpan: 3 } },
    { id: 'pub-next', type: 'next_fixtures', order: 1, config: { title: 'Next fixtures', limit: 8, scope: 'league_wide', league: 'PDL', season: '', fixtureIds: [], mdColSpan: 2, lgColSpan: 2 } },
    { id: 'pub-table', type: 'league_table', order: 2, config: { title: 'League table', league: 'PDL', season: '', limit: 10, mdColSpan: 1, lgColSpan: 1 } },
    { id: 'pub-milestones', type: 'league_milestones', order: 3, config: { title: 'League milestones', league: 'PDL', season: '', mdColSpan: 1, lgColSpan: 1 } },
    { id: 'pub-title', type: 'title_race', order: 4, config: { title: 'Title race', league: 'PDL', season: '', limit: 6, mdColSpan: 1, lgColSpan: 1 } },
    { id: 'pub-tons', type: 'tons_race', order: 5, config: { title: 'Tons race', league: 'PDL', season: '', limit: 8, mdColSpan: 1, lgColSpan: 1 } },
  ];
}

Alpine.data('dashboardLayoutBuilder', () => ({
  tabs: PAGE_TABS,
  activeKey: 'public_home',
  /** `default` | `combined` | `league:PDL` — saved separately per account (see API). */
  layoutKey: 'default',
  /** When layoutKey is `league:PDL`, mirrors the per-league dropdown. */
  layoutLeaguePick: '',
  widgets: [],
  isDefault: false,
  loading: true,
  saving: false,
  error: '',
  okMsg: '',
  addOpen: false,
  newType: 'message',

  typeLabels: TYPE_LABELS,

  metaLoading: false,
  leagueOptions: [],
  fixtureOptions: [],
  seasonOptions: [],
  metaSeason: '',

  init() {
    this.loadMeta();
    this.load();
  },

  layoutQuery() {
    return `?layoutKey=${encodeURIComponent(this.layoutKey || 'default')}`;
  },

  showLayoutScopeTabs() {
    return this.activeKey === 'public_home' || this.activeKey === 'player_home' || this.activeKey === 'admin_stats';
  },

  /** Quick opens in a new tab while editing layouts (full documentation can live elsewhere later). */
  layoutPreviewLinks() {
    return [
      { label: 'Public home', href: '/', title: 'Public marketing / guest landing page' },
      { label: 'Player home', href: '/home.html', title: 'Signed-in player home (layout strip when logged in)' },
      { label: 'Admin dashboard', href: '/admin/dashboard', title: 'Admin home and league-tab dashboard widgets' },
    ];
  },

  scopeMainLabel() {
    return this.activeKey === 'admin_stats' ? 'Account template' : 'Default';
  },

  setLayoutKey(key) {
    const next = String(key || 'default').trim() || 'default';
    if (next === this.layoutKey) return;
    this.layoutKey = next;
    if (next.startsWith('league:')) this.layoutLeaguePick = next.slice('league:'.length);
    else this.layoutLeaguePick = '';
    this.load();
  },

  onLeagueLayoutPick() {
    const v = String(this.layoutLeaguePick || '').trim();
    if (!v) return;
    this.setLayoutKey(`league:${v}`);
  },

  async loadMeta() {
    this.metaLoading = true;
    try {
      const res = await apiFetch('/dashboard-layout/meta');
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || res.statusText);
      this.leagueOptions = Array.isArray(j.leagueOptions) ? j.leagueOptions : [];
      this.fixtureOptions = Array.isArray(j.fixtureOptions) ? j.fixtureOptions : [];
      this.seasonOptions = Array.isArray(j.seasonOptions) ? j.seasonOptions.map(String) : [];
      this.metaSeason = String(j.season || '');
    } catch {
      this.leagueOptions = [];
      this.fixtureOptions = [];
      this.seasonOptions = [];
    } finally {
      this.metaLoading = false;
    }
  },

  async load() {
    this.loading = true;
    this.error = '';
    this.okMsg = '';
    try {
      const res = await apiFetch(`/dashboard-layout/${this.activeKey}${this.layoutQuery()}`);
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || res.statusText);
      if (j.layoutKey) {
        this.layoutKey = String(j.layoutKey);
        if (this.layoutKey.startsWith('league:')) this.layoutLeaguePick = this.layoutKey.slice('league:'.length);
        else this.layoutLeaguePick = '';
      }
      this.widgets = Array.isArray(j.widgets) ? j.widgets.map((w) => structuredClone(w)) : [];
      if (this.activeKey === 'admin_home') {
        this.ensureAdminHomeSections();
        this.ensureControlPanelShortcutFieldsOnWidgets();
      }
      this.isDefault = !!j.isDefault;
    } catch (e) {
      this.error = e?.message || 'Failed to load';
      this.widgets = [];
    } finally {
      this.loading = false;
    }
  },

  switchTab(key) {
    if (this.activeKey === key) return;
    this.activeKey = key;
    this.layoutKey = 'default';
    this.layoutLeaguePick = '';
    this.load();
  },

  sorted() {
    return [...this.widgets].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.id).localeCompare(String(b.id)));
  },

  reorder(fromIdx, dir) {
    const list = this.sorted();
    const j = fromIdx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[fromIdx];
    const b = list[j];
    const oa = a.order ?? fromIdx;
    const ob = b.order ?? j;
    a.order = ob;
    b.order = oa;
    this.widgets = [...this.widgets];
  },

  remove(id) {
    if (!confirm('Remove this widget?')) return;
    this.widgets = this.widgets.filter((w) => w.id !== id);
    this.renumber();
  },

  renumber() {
    this.sorted().forEach((w, i) => {
      w.order = i;
    });
  },

  openAdd() {
    this.addOpen = true;
    this.newType = 'message';
  },

  closeAdd() {
    this.addOpen = false;
  },

  addWidget() {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID ? `w-${crypto.randomUUID().slice(0, 8)}` : `w-${Date.now()}`;
    const order = this.widgets.length;
    const type = this.newType;
    this.widgets.push({ id, type, order, config: makeDefaultConfig(type, this.activeKey) });
    this.renumber();
    this.addOpen = false;
  },

  resetDefaults() {
    if (!confirm('Replace the current layout with the built-in default?')) return;
    this.widgets = defaultWidgetsFor(this.activeKey, this.layoutKey).map((w) => structuredClone(w));
    if (this.activeKey === 'admin_home') {
      this.ensureAdminHomeSections();
      this.ensureControlPanelShortcutFieldsOnWidgets();
    }
    this.isDefault = true;
  },

  ensureControlPanelShortcutFieldsOnWidgets() {
    for (const w of this.widgets) {
      if (String(w?.config?.sectionKey || '') !== 'control_panel') continue;
      w.config = w.config && typeof w.config === 'object' ? w.config : {};
      if (w.config.shortcutTarget == null) w.config.shortcutTarget = '';
      w.config.shortcutTarget = String(w.config.shortcutTarget || '');
      if (w.config.shortcutTitle == null) w.config.shortcutTitle = '';
      if (w.config.shortcutIcon == null) w.config.shortcutIcon = '';
    }
  },

  controlPanelShortcutTargetOptions() {
    return [
      { value: '', label: '— Empty slot (prompt on dashboard) —' },
      { value: 'venue_kiosk', label: 'Venue kiosk (new tab + QR)' },
      { value: 'league_builder', label: 'League & fixtures builder' },
      { value: 'venues', label: 'Venues' },
      { value: 'league_rules', label: 'League rules' },
      { value: 'teams', label: 'Teams' },
      { value: 'players', label: 'Players' },
      { value: 'fixtures', label: 'Fixtures (header league)' },
      { value: 'league_table', label: 'League table (header league)' },
      { value: 'challenges', label: 'Challenges' },
      { value: 'training', label: 'Training' },
      { value: 'account_layout', label: 'This layout editor' },
    ];
  },

  controlPanelShortcutIconOptions() {
    return [
      { value: '', label: 'Auto (from destination)' },
      { value: 'fa-tablet-screen-button', label: 'Tablet / kiosk' },
      { value: 'fa-wand-magic-sparkles', label: 'Magic / builder' },
      { value: 'fa-location-dot', label: 'Location' },
      { value: 'fa-list-check', label: 'Checklist' },
      { value: 'fa-people-group', label: 'People' },
      { value: 'fa-user-plus', label: 'Add player' },
      { value: 'fa-calendar-days', label: 'Calendar' },
      { value: 'fa-ranking-star', label: 'Ranking' },
      { value: 'fa-trophy', label: 'Trophy' },
      { value: 'fa-bullseye', label: 'Bullseye' },
      { value: 'fa-table-columns', label: 'Layout' },
      { value: 'fa-gear', label: 'Gear' },
      { value: 'fa-link', label: 'Link' },
    ];
  },

  ensureAdminHomeSections() {
    if (this.activeKey !== 'admin_home') return;
    const wanted = [
      { id: 'admin-home-control-panel', sectionKey: 'control_panel', title: 'Control panel quick links', tone: 'brand' },
      { id: 'admin-home-challenges-training', sectionKey: 'challenges_training', title: 'Challenges & training', tone: 'neutral' },
      { id: 'admin-home-plan-shortcuts', sectionKey: 'plan_shortcuts', title: 'Plan shortcuts', tone: 'neutral' },
      { id: 'admin-home-subscription', sectionKey: 'subscription', title: 'Subscription', tone: 'neutral' },
      { id: 'admin-home-backup-restore', sectionKey: 'backup_restore', title: 'Backup & restore', tone: 'neutral' },
      { id: 'admin-home-upcoming-by-league', sectionKey: 'upcoming_by_league', title: 'Upcoming by league', tone: 'neutral' },
      { id: 'admin-home-admin-stats', sectionKey: 'admin_stats', title: 'Admin stats', tone: 'neutral' },
    ];
    const sectionKeys = new Set(wanted.map((w) => w.sectionKey));
    const bySection = new Map(
      (Array.isArray(this.widgets) ? this.widgets : [])
        .map((w) => [String(w?.config?.sectionKey || ''), w])
        .filter(([k]) => sectionKeys.has(k)),
    );
    const next = [];
    for (const [idx, spec] of wanted.entries()) {
      const existing = bySection.get(spec.sectionKey);
      if (existing) {
        const existingConfig = existing.config && typeof existing.config === 'object' ? existing.config : null;
        existing.type = 'message';
        existing.order = Number(existing.order ?? idx);
        existing.config = {
          ...(existingConfig || undefined),
          sectionKey: spec.sectionKey,
          title: String(existingConfig?.title || spec.title),
          body: '',
          tone: String(existingConfig?.tone || spec.tone),
          enabled: existingConfig?.enabled !== false,
          mdColSpan: 2,
          lgColSpan: 3,
          shortcutTarget: String(existingConfig?.shortcutTarget ?? ''),
          shortcutTitle: String(existingConfig?.shortcutTitle ?? ''),
          shortcutIcon: String(existingConfig?.shortcutIcon ?? ''),
        };
        next.push(existing);
      } else {
        next.push({
          id: spec.id,
          type: 'message',
          order: idx,
          config: {
            sectionKey: spec.sectionKey,
            enabled: true,
            title: spec.title,
            body: '',
            tone: spec.tone,
            mdColSpan: 2,
            lgColSpan: 3,
            shortcutTarget: '',
            shortcutTitle: '',
            shortcutIcon: '',
          },
        });
      }
    }
    this.widgets = next;
    this.renumber();
  },

  adminSectionLabel(widget) {
    const key = String(widget?.config?.sectionKey || '');
    if (key === 'control_panel') return 'Control panel quick links';
    if (key === 'challenges_training') return 'Challenges & training';
    if (key === 'plan_shortcuts') return 'Plan shortcuts';
    if (key === 'subscription') return 'Subscription';
    if (key === 'backup_restore') return 'Backup & restore';
    if (key === 'upcoming_by_league') return 'Upcoming by league';
    if (key === 'admin_stats') return 'Admin stats';
    return 'Dashboard section';
  },

  adminSectionPreviewText(widget) {
    const enabled = widget?.config?.enabled !== false;
    return enabled ? 'Visible on /admin/dashboard' : 'Hidden from /admin/dashboard';
  },

  adminSectionPreviewClass(widget) {
    const enabled = widget?.config?.enabled !== false;
    return enabled
      ? 'border-emerald-400/35 bg-emerald-500/10 text-emerald-100'
      : 'border-white/15 bg-black/25 text-neutral-400';
  },

  async save() {
    this.saving = true;
    this.error = '';
    this.okMsg = '';
    try {
      this.renumber();
      const res = await apiFetch(`/dashboard-layout/${this.activeKey}${this.layoutQuery()}`, {
        method: 'PUT',
        body: { widgets: this.sorted() },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || res.statusText);
      if (j.layoutKey) this.layoutKey = String(j.layoutKey);
      this.widgets = Array.isArray(j.widgets) ? j.widgets.map((w) => structuredClone(w)) : this.widgets;
      if (this.activeKey === 'admin_home') this.ensureControlPanelShortcutFieldsOnWidgets();
      this.isDefault = false;
      this.okMsg = 'Saved.';
      setTimeout(() => {
        this.okMsg = '';
      }, 3500);
    } catch (e) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.saving = false;
    }
  },

  addLink(widget) {
    widget.config.links = widget.config.links || [];
    widget.config.links.push({ label: 'New link', href: '/', icon: 'fa-arrow-right' });
  },

  removeLink(widget, idx) {
    widget.config.links.splice(idx, 1);
  },

  fixtureIdsText(widget) {
    const arr = Array.isArray(widget.config.fixtureIds) ? widget.config.fixtureIds : [];
    return arr.join('\n');
  },

  setFixtureIdsText(widget, text) {
    widget.config.fixtureIds = String(text || '')
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  },

  addFixtureToWidget(widget, fixtureId) {
    if (!fixtureId) return;
    widget.config.fixtureIds = Array.isArray(widget.config.fixtureIds) ? widget.config.fixtureIds : [];
    if (!widget.config.fixtureIds.includes(fixtureId)) widget.config.fixtureIds.push(fixtureId);
  },

  removeFixtureFromWidget(widget, fixtureId) {
    widget.config.fixtureIds = (Array.isArray(widget.config.fixtureIds) ? widget.config.fixtureIds : []).filter((id) => id !== fixtureId);
  },

  fixtureLabel(id) {
    return this.fixtureOptions.find((f) => String(f.id) === String(id))?.label || id;
  },

  ensureLayoutDefaults(widget) {
    widget.config = widget.config || {};
    if (![1, 2].includes(Number(widget.config.mdColSpan))) widget.config.mdColSpan = 1;
    const lg = Number(widget.config.lgColSpan);
    if (![1, 2, 3].includes(lg)) widget.config.lgColSpan = Math.max(1, Number(widget.config.mdColSpan || 1));
  },

  needsLeague(widget) {
    return ['league_table', 'league_milestones', 'title_race', 'tons_race', 'featured_fixtures', 'next_fixtures', 'player_summary'].includes(widget.type);
  },

  needsLimit(widget) {
    return ['league_table', 'title_race', 'tons_race', 'featured_fixtures', 'next_fixtures'].includes(widget.type);
  },

  needsFixturePicker(widget) {
    return ['featured_fixtures', 'next_fixtures'].includes(widget.type);
  },
}));
